import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import PromoCode from 'services/PromoCode'
import Select from 'components/Inputs/Select'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import DateInput from 'components/Inputs/DateInput'
import MainButton from 'components/Buttons/MainButton'
import promoCodeValidator from 'validators/promoCodeValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  TextArea,
  Row,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function AddPromoCodeModal ({
  visible,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [errorMessage, setErrorMessage] = useState (null)
  const [plans, setPlans] = useState ([])
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getPlans ()
  }, [])

  const getPlans = async () => {
    try {
      const { data: plans } = await cutileiApi.get (
        '/plans?status=active', requestConfig
      )

      setPlans (plans.map (plan => ({
        label: plan.name,
        value: plan
      })))
    } catch (error) {
      console.error ('Failed to fetch plans:', error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Criar cupom promocional</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={promoCodeValidator}
            initialValues={{
              'code': '',
              'description': '',
              'shortDescription': '',
              'availableAmount': 1,
              'maxSchedules': null,
              'expiresAt': '',
              'discount_type': null,
              'plan': null,
              'value': null,
              'percentage': null,
              'free_days': null,
              'cycles': null
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setErrorMessage (null)
              setSubmitting (true)

              try {
                const {
                  code,
                  description,
                  shortDescription,
                  availableAmount,
                  maxSchedules,
                  expiresAt,
                  ...discountData
                } = values

                const promoCodeData = {
                  code,
                  description,
                  shortDescription,
                  availableAmount,
                  expiresAt,
                  maxSchedules: discountData.plan.value.is_trial ? maxSchedules : null,
                  discount: {
                    planId: discountData.plan.value.id,
                    type: discountData.discount_type.value,
                    value: discountData.value,
                    percentage: discountData.percentage,
                    free_days: discountData.free_days,
                    cycles: discountData.cycles
                  }
                }

                const { data: promoCode } = await cutileiApi.post (
                  '/promo_codes', promoCodeData, requestConfig
                )

                setSubmitting (false)
                handleConfirm (promoCode)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                logApiError (error)
              }
            }}
          >
            {function AddPromoCodeForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur, 
              handleSubmit
            }) {
              return (
                <>
                  <DialogBody>
                    <Form>
                      <Row>
                        <FormField stretch={2}>
                          <Label htmlFor='code'>
                            Código do cupom
                          </Label>
                          <Input
                            id='code'
                            placeholder='Código do cupom'
                            value={values.code}
                            onChange={e => setFieldValue ('code', e.target.value.toUpperCase ())}
                            onBlur={handleBlur ('code')}
                          />
                          {touched.code && errors.code && (
                            <WarningText>
                              {errors.code}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Label htmlFor='availableAmount'>
                            Qtd. disponível
                          </Label>
                          <NumberInput
                            id='availableAmount'
                            name='availableAmount'
                            placeholder='Qtd. disponível'
                            value={values.availableAmount}
                            minValue={1}
                            maxValue={999999}
                            onChange={setFieldValue}
                            onBlur={handleBlur}
                            style={{width: '100%'}}
                          />
                        </FormField>
                      </Row>
                      <Row>
                        <FormField stretch={2}>
                          <Label htmlFor='plan'>
                            Plano
                          </Label>
                          <Select
                            id='plan'
                            name='plan'
                            placeholder='Selecionar...'
                            value={values.plan}
                            options={plans}
                            onChange={(name, plan) => {
                              setFieldValue (name, plan)
                              setFieldValue (
                                'maxSchedules',
                                plan?.value.is_trial ? (values.maxSchedules ?? 1) : null
                              )
                            }}
                            onBlur={setFieldTouched}
                            error={errors.plan}
                            touched={touched.plan}
                          />
                          {touched.plan && errors.plan && (
                            <WarningText>
                              {errors.plan}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField stretch={1}>
                          <Label htmlFor='expiresAt'>
                            Válido até
                          </Label>
                          <DateInput
                            value={values.expiresAt}
                            placeholder='Data de validade'
                            onChange={date => setFieldValue ('expiresAt', date)}
                            style={{width: '100%'}}
                          />
                          {touched.expiresAt && errors.expiresAt && (
                            <WarningText>
                              {errors.expiresAt}
                            </WarningText>
                          )}
                        </FormField>
                        {values.plan?.value.is_trial && (
                          <FormField>
                            <Label htmlFor='maxSchedules'>
                              Qtd. de agendamentos
                            </Label>
                            <NumberInput
                              id='maxSchedules'
                              name='maxSchedules'
                              value={values.maxSchedules}
                              minValue={1}
                              maxValue={99}
                              onChange={setFieldValue}
                              onBlur={handleBlur}
                              style={{width: '100%'}}
                            />
                          </FormField>
                        )}
                      </Row>
                      <Row>
                        <FormField>
                          <Label htmlFor='description'>
                            Descrição
                          </Label>
                          <TextArea
                            id='description'
                            placeholder='Texto que vai aparecer no pop-up quando o cupom for aplicado'
                            rows={4}
                            value={values.description}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <Label htmlFor='short_description'>
                            Descrição resumida
                          </Label>
                          <TextArea
                            id='short_description'
                            placeholder='Texto que vai aparecer na tela de checkout abaixo do campo do cupom'
                            rows={2}
                            value={values.shortDescription}
                            onChange={handleChange ('shortDescription')} 
                            onBlur={handleBlur ('shortDescription')}
                          />
                          {touched.shortDescription && errors.shortDescription && (
                            <WarningText>
                              {errors.shortDescription}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <Label htmlFor='discount_type'>
                            Tipo de desconto
                          </Label>
                          <Select
                            id='discount_type'
                            name='discount_type'
                            placeholder='Selecionar...'
                            value={values.discount_type}
                            options={PromoCode.TYPE_OPTIONS}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            error={errors.discount_type}
                            touched={touched.discount_type}
                          />
                          {touched.discount_type && errors.discount_type && (
                            <WarningText>
                              {errors.discount_type}
                            </WarningText>
                          )}
                        </FormField>
                        {values.discount_type?.value === 'value' && (
                          <FormField>
                            <Label htmlFor='value'>
                              Valor (R$)
                            </Label>
                            <MoneyInput
                              id='value'
                              name='value'
                              placeholder='Valor'
                              value={values.value}
                              onValueChange={setFieldValue}
                              onBlur={handleBlur ('value')}
                            />
                            {touched.value && errors.value && (
                              <WarningText>
                                {errors.value}
                              </WarningText>
                            )}
                          </FormField>
                        )}
                        {values.discount_type?.value === 'percentage' && (
                          <FormField>
                            <Label htmlFor='percentage'>
                              Porcentagem (%)
                            </Label>
                            <NumberInput
                              id='percentage'
                              name='percentage'
                              placeholder='Porcentagem'
                              value={values.percentage}
                              minValue={1}
                              maxValue={100}
                              onChange={setFieldValue}
                              onBlur={handleBlur}
                              style={{width: '100%'}}
                            />
                          </FormField>
                        )}
                        {values.discount_type?.value === 'free_days' && (
                          <FormField>
                            <Label htmlFor='free_days'>
                              Dias gratuitos
                            </Label>
                            <NumberInput
                              id='free_days'
                              name='free_days'
                              placeholder='Dias gratuitos'
                              value={values.free_days}
                              minValue={1}
                              maxValue={99}
                              onChange={setFieldValue}
                              onBlur={handleBlur}
                              style={{width: '100%'}}
                            />
                          </FormField>
                        )}
                        {['value', 'percentage'].includes (values.discount_type?.value) && (
                          <FormField>
                            <Label htmlFor='cycles'>
                              Mêses
                            </Label>
                            <NumberInput
                              id='cycles'
                              name='cycles'
                              placeholder='Mêses'
                              value={values.cycles}
                              minValue={1}
                              maxValue={99}
                              onChange={setFieldValue}
                              onBlur={handleBlur}
                              style={{width: '100%'}}
                            />
                          </FormField>
                        )}
                      </Row>
                      {errorMessage && (
                        <ErrorContainer>
                          <p>{errorMessage}</p>
                        </ErrorContainer>
                      )}
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Criar cupom'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={160}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
